import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Send, Eye, Mail, History } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { logger } from '@/lib/logger';

type RecipientFilter = 'all' | 'no_subscription' | 'active' | 'manual';

const FILTER_LABELS: Record<RecipientFilter, string> = {
  all: 'All Users',
  no_subscription: 'No Subscription',
  active: 'Active Subscribers',
  manual: 'Manually Selected',
};

interface ManualUser {
  user_id: string;
  email: string;
  full_name: string | null;
}

interface BulkEmail {
  id: string;
  subject: string;
  html_content: string;
  sent_by: string | null;
  sent_at: string;
  recipient_count: number;
  status: 'pending' | 'sending' | 'completed' | 'failed';
  error_message: string | null;
}

export function BulkEmailManager() {
  const { toast } = useToast();
  const [subject, setSubject] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bulkEmails, setBulkEmails] = useState<BulkEmail[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [userCount, setUserCount] = useState<number | null>(null);
  const [recipientFilter, setRecipientFilter] = useState<RecipientFilter>('all');
  const [manualUsers, setManualUsers] = useState<ManualUser[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [loadingManualUsers, setLoadingManualUsers] = useState(false);
  const [manualSearch, setManualSearch] = useState('');

  useEffect(() => {
    loadBulkEmails();
  }, []);

  useEffect(() => {
    setSelectedUserIds(new Set());
    setManualSearch('');
    if (recipientFilter === 'manual') {
      loadManualUsers();
    } else {
      loadUserCount();
    }
  }, [recipientFilter]);

  const loadUserCount = async () => {
    try {
      if (recipientFilter === 'all') {
        const { count, error } = await supabase
          .from('profiles')
          .select('*', { count: 'exact', head: true })
          .not('email', 'is', null);
        if (error) throw error;
        setUserCount(count || 0);
      } else {
        // Fetch profiles with their team's subscription info
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('user_id, team_id')
          .not('email', 'is', null);
        if (profilesError) throw profilesError;

        const { data: teamSubs, error: subsError } = await supabase
          .from('team_subscriptions')
          .select('team_id, polar_subscription_id, status')
          .not('polar_subscription_id', 'is', null);
        if (subsError) throw subsError;

        const activeTeams = new Set(
          teamSubs
            ?.filter(s => s.status === 'active' || s.status === 'trialing')
            .map(s => s.team_id)
        );
        const teamsWithAnySub = new Set(teamSubs?.map(s => s.team_id));

        if (recipientFilter === 'no_subscription') {
          setUserCount(profiles?.filter(p => !teamsWithAnySub.has(p.team_id)).length || 0);
        } else {
          // active
          setUserCount(profiles?.filter(p => activeTeams.has(p.team_id)).length || 0);
        }
      }
    } catch (error) {
      logger.error('Error loading user count:', error);
    }
  };

  const loadManualUsers = async () => {
    setLoadingManualUsers(true);
    try {
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('user_id, full_name, email, team_id')
        .not('email', 'is', null);
      if (profilesError) throw profilesError;

      const { data: teamSubs, error: subsError } = await supabase
        .from('team_subscriptions')
        .select('team_id, status')
        .not('polar_subscription_id', 'is', null);
      if (subsError) throw subsError;

      const activeTeams = new Set(
        teamSubs
          ?.filter(s => s.status === 'active' || s.status === 'trialing')
          .map(s => s.team_id)
      );

      const activeProfiles = (profiles || [])
        .filter(p => activeTeams.has(p.team_id))
        .sort((a, b) => (a.full_name || a.email).localeCompare(b.full_name || b.email));

      setManualUsers(activeProfiles);
    } catch (error) {
      logger.error('Error loading manual users:', error);
    } finally {
      setLoadingManualUsers(false);
    }
  };

  const loadBulkEmails = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('bulk_emails')
        .select('*')
        .order('sent_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      setBulkEmails(data || []);
    } catch (error: any) {
      logger.error('Error loading bulk emails:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to load bulk email history',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = () => {
    setShowPreview(true);
  };

  const getPreviewHtml = () => {
    const NORTH_LOGO_URL = "https://zkqcqgjbsigjdwzmnwmp.supabase.co/storage/v1/object/public/logos/north-logo.png";
    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${subject || 'Update from NORTH'}</title>
</head>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; background-color: #f5f5f5;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f5f5f5; padding: 40px 20px;">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
          <tr>
            <td style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 40px 40px 30px; text-align: center;">
              <h1 style="color: #ffffff; margin: 0; font-size: 32px; font-weight: 600;">Update from NORTH</h1>
            </td>
          </tr>
          <tr>
            <td style="padding: 30px 40px 0;">
              <img src="${NORTH_LOGO_URL}" alt="NORTH Logo" style="height: 40px; width: auto; display: block;" />
            </td>
          </tr>
          <tr>
            <td style="padding: 20px 40px 40px;">
              <p style="color: #333333; font-size: 16px; line-height: 24px; margin: 0 0 20px;">Hi [Recipient Name],</p>
              <div style="color: #333333; font-size: 16px; line-height: 24px;">
                ${htmlContent || '<p style="color: #999999; font-style: italic;">Your email content will appear here...</p>'}
              </div>
              <p style="color: #666666; font-size: 14px; line-height: 22px; margin: 30px 0 0;">Best regards,<br>The NORTH Team</p>
            </td>
          </tr>
          <tr>
            <td style="background-color: #f8f9fa; padding: 20px; text-align: center;">
              <p style="color: #999999; font-size: 12px; margin: 0;">© ${new Date().getFullYear()} NORTH. All rights reserved.</p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>
    `;
  };

  const handleSend = async () => {
    if (!subject.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Subject is required',
        variant: 'destructive',
      });
      return;
    }

    if (!htmlContent.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Email content is required',
        variant: 'destructive',
      });
      return;
    }

    if (recipientFilter === 'manual' && selectedUserIds.size === 0) {
      toast({
        title: 'Validation Error',
        description: 'Please select at least one recipient',
        variant: 'destructive',
      });
      return;
    }

    setShowConfirmDialog(true);
  };

  const confirmSend = async () => {
    setShowConfirmDialog(false);
    setSending(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase.functions.invoke('send-bulk-email', {
        body: {
          subject: subject.trim(),
          html_content: htmlContent.trim(),
          userId: user.id,
          recipientFilter,
          ...(recipientFilter === 'manual' ? { selectedUserIds: Array.from(selectedUserIds) } : {}),
        },
      });

      if (error) throw error;

      toast({
        title: 'Success!',
        description: data.message || 'Bulk email sent successfully',
      });

      // Clear form
      setSubject('');
      setHtmlContent('');

      // Reload history
      loadBulkEmails();
    } catch (error: any) {
      logger.error('Error sending bulk email:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to send bulk email',
        variant: 'destructive',
      });
    } finally {
      setSending(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive'> = {
      completed: 'default',
      sending: 'secondary',
      pending: 'secondary',
      failed: 'destructive',
    };
    return (
      <Badge variant={variants[status] || 'secondary'}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const effectiveCount = recipientFilter === 'manual' ? selectedUserIds.size : userCount;

  const filteredManualUsers = manualUsers.filter(u => {
    if (!manualSearch) return true;
    const q = manualSearch.toLowerCase();
    return u.email.toLowerCase().includes(q) || (u.full_name || '').toLowerCase().includes(q);
  });

  return (
    <div className="space-y-6">
      <Tabs defaultValue="compose" className="space-y-6">
        <TabsList>
          <TabsTrigger value="compose">Compose Email</TabsTrigger>
          <TabsTrigger value="history">Email History</TabsTrigger>
        </TabsList>

        <TabsContent value="compose">
          <Card>
            <CardHeader>
              <CardTitle>Send Bulk Email</CardTitle>
              <CardDescription>
                Send an email using North's email template styling
                {recipientFilter !== 'manual' && userCount !== null && (
                  <span className="block mt-1">
                    This will be sent to approximately <strong>{userCount}</strong> {FILTER_LABELS[recipientFilter].toLowerCase()}.
                  </span>
                )}
                {recipientFilter === 'manual' && selectedUserIds.size > 0 && (
                  <span className="block mt-1">
                    <strong>{selectedUserIds.size}</strong> recipient{selectedUserIds.size !== 1 ? 's' : ''} selected.
                  </span>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="recipientFilter">Recipients</Label>
                <Select
                  value={recipientFilter}
                  onValueChange={(value) => setRecipientFilter(value as RecipientFilter)}
                >
                  <SelectTrigger id="recipientFilter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    <SelectItem value="no_subscription">No Subscription — signed up but never subscribed</SelectItem>
                    <SelectItem value="active">Active Subscribers — active or trialing</SelectItem>
                    <SelectItem value="manual">Manually Select Recipients</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {recipientFilter === 'manual' && (
                <div className="space-y-3 border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">
                      Active Subscribers
                      {!loadingManualUsers && (
                        <span className="text-muted-foreground font-normal ml-1">({manualUsers.length})</span>
                      )}
                    </p>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedUserIds(new Set(filteredManualUsers.map(u => u.user_id)))}
                        disabled={loadingManualUsers || filteredManualUsers.length === 0}
                        className="h-7 text-xs"
                      >
                        Select All
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedUserIds(new Set())}
                        disabled={loadingManualUsers || selectedUserIds.size === 0}
                        className="h-7 text-xs"
                      >
                        Deselect All
                      </Button>
                    </div>
                  </div>
                  <Input
                    placeholder="Search by name or email..."
                    value={manualSearch}
                    onChange={(e) => setManualSearch(e.target.value)}
                    className="h-8 text-sm"
                  />
                  {loadingManualUsers ? (
                    <div className="flex items-center justify-center py-6">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : filteredManualUsers.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      {manualSearch ? 'No users match your search.' : 'No active subscribers found.'}
                    </p>
                  ) : (
                    <div className="max-h-60 overflow-y-auto space-y-1 pr-1">
                      {filteredManualUsers.map(user => (
                        <div
                          key={user.user_id}
                          className="flex items-center gap-3 py-1.5 px-1 rounded hover:bg-muted/50 cursor-pointer"
                          onClick={() => {
                            setSelectedUserIds(prev => {
                              const next = new Set(prev);
                              if (next.has(user.user_id)) {
                                next.delete(user.user_id);
                              } else {
                                next.add(user.user_id);
                              }
                              return next;
                            });
                          }}
                        >
                          <Checkbox
                            id={`user-${user.user_id}`}
                            checked={selectedUserIds.has(user.user_id)}
                            onCheckedChange={(checked) => {
                              setSelectedUserIds(prev => {
                                const next = new Set(prev);
                                if (checked) {
                                  next.add(user.user_id);
                                } else {
                                  next.delete(user.user_id);
                                }
                                return next;
                              });
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <label
                            htmlFor={`user-${user.user_id}`}
                            className="text-sm cursor-pointer flex-1 min-w-0 pointer-events-none"
                          >
                            <span className="font-medium">{user.full_name || user.email.split('@')[0]}</span>
                            <span className="text-muted-foreground ml-2 text-xs">{user.email}</span>
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                  {selectedUserIds.size > 0 && (
                    <p className="text-xs text-muted-foreground pt-1 border-t">
                      {selectedUserIds.size} user{selectedUserIds.size !== 1 ? 's' : ''} selected
                    </p>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="subject">Subject *</Label>
                <Input
                  id="subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Email subject line"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="content">Email Content (HTML) *</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handlePreview}
                    disabled={!htmlContent.trim()}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Preview
                  </Button>
                </div>
                <Textarea
                  id="content"
                  value={htmlContent}
                  onChange={(e) => setHtmlContent(e.target.value)}
                  placeholder="Enter your email content in HTML format. You can use basic HTML tags like &lt;p&gt;, &lt;strong&gt;, &lt;em&gt;, &lt;ul&gt;, &lt;li&gt;, etc. The content will be wrapped in North's email template."
                  rows={12}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Tip: Use HTML tags for formatting. The recipient's name will be automatically inserted as "Hi [Name],"
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleSend}
                  disabled={
                    sending ||
                    !subject.trim() ||
                    !htmlContent.trim() ||
                    (recipientFilter === 'manual' && selectedUserIds.size === 0)
                  }
                  className="flex-1"
                >
                  {sending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      Send to {effectiveCount !== null ? `${effectiveCount} ` : ''}{FILTER_LABELS[recipientFilter]}
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle>Email History</CardTitle>
              <CardDescription>
                View past bulk email campaigns
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center p-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : bulkEmails.length === 0 ? (
                <Alert>
                  <AlertDescription>
                    No bulk emails sent yet.
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="space-y-4">
                  {bulkEmails.map((email) => (
                    <Card key={email.id}>
                      <CardContent className="pt-6">
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <h3 className="font-semibold">{email.subject}</h3>
                            {getStatusBadge(email.status)}
                          </div>
                          <div className="text-sm text-muted-foreground space-y-1">
                            <p>
                              Sent: {new Date(email.sent_at).toLocaleString()}
                            </p>
                            <p>
                              Recipients: {email.recipient_count}
                            </p>
                            {email.error_message && (
                              <p className="text-destructive text-xs">
                                Error: {email.error_message}
                              </p>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground mt-2 p-2 bg-muted rounded">
                            <div
                              dangerouslySetInnerHTML={{
                                __html: email.html_content.substring(0, 200) + (email.html_content.length > 200 ? '...' : ''),
                              }}
                            />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Email Preview</DialogTitle>
            <DialogDescription>
              This is how your email will appear to recipients
            </DialogDescription>
          </DialogHeader>
          <div className="border rounded-lg overflow-hidden">
            <iframe
              srcDoc={getPreviewHtml()}
              className="w-full h-[600px] border-0"
              title="Email Preview"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Bulk Email</DialogTitle>
            <DialogDescription>
              Are you sure you want to send this email to {effectiveCount !== null ? effectiveCount : ''} {FILTER_LABELS[recipientFilter].toLowerCase()}?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            <p className="text-sm font-medium">Subject:</p>
            <p className="text-sm text-muted-foreground">{subject}</p>
            <p className="text-sm font-medium mt-4">This action cannot be undone.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button onClick={confirmSend} disabled={sending}>
              {sending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Send Email
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
